import React, {
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { catalogApi, providerApi, getAccessToken } from "../../api/client";

export default function ProviderCreate() {
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCat, setSubcategoriesByCat] = useState({});
  const [forms, setForms] = useState([
    {
      name: "",
      price: "",
      description: "",
      phoneNumber: "",
      address: "",
      locationLat: "",
      locationLng: "",
      categoryId: "",
      subcategoryId: "",
      maxRadiusKm: "",
      images: [],
    },
  ]);
  const [errors, setErrors] = useState([{}]);
  const [generalError, setGeneralError] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [appearingIndex, setAppearingIndex] = useState(null);
  const [removingIndex, setRemovingIndex] = useState(null);
  const [formEntering, setFormEntering] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([[]]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [locationTextByForm, setLocationTextByForm] = useState([""]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const locationRequestSeq = useRef(0);
  const locationCacheRef = useRef(new Map());
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [defaultAddress, setDefaultAddress] = useState("");
  const [defaultPhoneNumber, setDefaultPhoneNumber] = useState("");
  const [defaultLocationLat, setDefaultLocationLat] = useState("");
  const [defaultLocationLng, setDefaultLocationLng] = useState("");

  // Load provider profile to check verified listings and get default values from existing listings
  useEffect(() => {
    (async () => {
      try {
        setProfileLoading(true);
        const response = await providerApi.getMyProfile();
        const profileData = response?.providerProfile || response;
        setProfile(profileData);

        // Fetch ALL existing listings (any status) to get default values for prefilling
        // This works even if listings are not verified yet
        try {
          const listingsResponse = await providerApi.myListings({});
          const existingListings = listingsResponse?.items || [];

          // Find first listing with address and phone number (location is optional)
          const listingWithDefaults = existingListings.find(
            (listing) => listing.address && listing.phoneNumber
          );

          if (listingWithDefaults) {
            const address = listingWithDefaults.address || "";
            const phoneNumber = listingWithDefaults.phoneNumber || "";
            const locationLat = listingWithDefaults.locationLat || "";
            const locationLng = listingWithDefaults.locationLng || "";

            setDefaultAddress(address);
            setDefaultPhoneNumber(phoneNumber);
            if (locationLat && locationLng) {
              setDefaultLocationLat(locationLat);
              setDefaultLocationLng(locationLng);
            }
          }
        } catch (listingsError) {
          console.error("Failed to fetch existing listings:", listingsError);
          // Continue without defaults
        }
      } catch (error) {
        console.error("Failed to load provider profile:", error);
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, []);

  // Prefill all forms (existing and future) with default values when they become available
  // This effect ensures forms are prefilled even after initial render, which is crucial for mobile
  useEffect(() => {
    // Check if we have any default values available
    const hasDefaults =
      (defaultAddress && defaultAddress.trim()) ||
      (defaultPhoneNumber && defaultPhoneNumber.trim()) ||
      (defaultLocationLat && defaultLocationLat.trim()) ||
      (defaultLocationLng && defaultLocationLng.trim());

    if (!hasDefaults) return;

    // Use requestAnimationFrame for mobile browsers to ensure DOM is ready
    const prefillTimer = requestAnimationFrame(() => {
      setForms((prevForms) =>
        prevForms.map((form) => {
          // Check if fields are truly empty (empty string, null, undefined, or whitespace only)
          const isEmptyAddress =
            !form.address ||
            (typeof form.address === "string" && form.address.trim() === "");
          const isEmptyPhone =
            !form.phoneNumber ||
            (typeof form.phoneNumber === "string" &&
              form.phoneNumber.trim() === "");
          const isEmptyLat =
            !form.locationLat ||
            (typeof form.locationLat === "string" &&
              form.locationLat.trim() === "");
          const isEmptyLng =
            !form.locationLng ||
            (typeof form.locationLng === "string" &&
              form.locationLng.trim() === "");

          return {
            ...form,
            // Prefill only if field is empty and we have a default value
            address:
              isEmptyAddress && defaultAddress && defaultAddress.trim()
                ? defaultAddress.trim()
                : form.address || "",
            phoneNumber:
              isEmptyPhone && defaultPhoneNumber && defaultPhoneNumber.trim()
                ? defaultPhoneNumber.trim()
                : form.phoneNumber || "",
            locationLat:
              isEmptyLat && defaultLocationLat && defaultLocationLat.trim()
                ? defaultLocationLat.trim()
                : form.locationLat || "",
            locationLng:
              isEmptyLng && defaultLocationLng && defaultLocationLng.trim()
                ? defaultLocationLng.trim()
                : form.locationLng || "",
          };
        })
      );
    });

    return () => cancelAnimationFrame(prefillTimer);
  }, [
    defaultAddress,
    defaultPhoneNumber,
    defaultLocationLat,
    defaultLocationLng,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const res = await catalogApi.listCategories();
        setCategories(res?.items || []);
      } catch (_e) {
        setCategories([]);
      }
    })();
  }, []);

  // Fetch location suggestions for active form using Nominatim
  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      const query = (locationTextByForm[activeIndex] || "").trim();
      // Wait for at least 3 characters to reduce flicker and API spam
      if (!query || query.length < 3) {
        setLocationOptions([]);
        if (locationLoading) setLocationLoading(false);
        return;
      }
      // Serve cached results immediately to avoid empty dropdown flicker
      const cached = locationCacheRef.current.get(query);
      if (cached && Array.isArray(cached)) {
        setLocationOptions(cached);
      }
      try {
        if (!cached) setLocationLoading(true);
        const mySeq = ++locationRequestSeq.current;
        const NOMINATIM_ZOOM = 10;
        const ALLOWED_PLACE_TYPES = new Set([
          "city",
          "town",
          "village",
          "suburb",
          "locality",
          "state",
          "district",
          "county",
        ]);
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&addressdetails=1&limit=5&countrycodes=in&zoom=${NOMINATIM_ZOOM}`;
        const res = await fetch(url, {
          headers: {
            Accept: "application/json",
            "User-Agent": "OnlineIndianMarket/1.0",
            "Accept-Language": "en-IN,en;q=0.8",
          },
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = await res.json();
        // Ignore stale responses to prevent flicker
        if (mySeq !== locationRequestSeq.current) return;
        const raw = Array.isArray(data) ? data : [];
        const narrowed = raw.filter((it) =>
          ALLOWED_PLACE_TYPES.has(String(it.type || ""))
        );
        const chosen = narrowed.length > 0 ? narrowed : raw;
        const opts = chosen.map((it) => ({
          label: it.display_name,
          lat: Number(it.lat),
          lng: Number(it.lon),
        }));
        // Update cache and options only if changed to reduce renders
        locationCacheRef.current.set(query, opts);
        setLocationOptions((prev) => {
          const sameLen = prev.length === opts.length;
          const sameAll =
            sameLen && prev.every((p, i) => p.label === opts[i].label);
          return sameAll ? prev : opts;
        });
      } catch (_e) {
        // ignore
      } finally {
        if (locationRequestSeq.current) setLocationLoading(false);
      }
    };
    const t = setTimeout(run, 350);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, locationTextByForm]);

  const setLocationText = (index, text) => {
    setLocationTextByForm((prev) => {
      const next = [...prev];
      next[index] = text;
      return next;
    });
  };

  const ensureSubcategories = async (categoryId) => {
    if (!categoryId || subcategoriesByCat[categoryId]) return;
    try {
      const res = await catalogApi.listSubcategories(categoryId);
      setSubcategoriesByCat((p) => ({ ...p, [categoryId]: res?.items || [] }));
    } catch (_e) {
      setSubcategoriesByCat((p) => ({ ...p, [categoryId]: [] }));
    }
  };

  const updateForm = (index, updater) => {
    setForms((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updater };
      return next;
    });
  };

  const updateError = (index, nextErrors) => {
    setErrors((prev) => {
      const arr = [...prev];
      arr[index] = nextErrors || {};
      return arr;
    });
  };

  const addListingForm = () => {
    if (forms.length >= 5) return;
    setForms((prev) => [
      ...prev,
      {
        name: "",
        price: "",
        description: "",
        // Prefill with default values from existing listings
        phoneNumber: defaultPhoneNumber || "",
        address: defaultAddress || "",
        locationLat: defaultLocationLat || "",
        locationLng: defaultLocationLng || "",
        categoryId: "",
        subcategoryId: "",
        maxRadiusKm: "",
        images: [],
      },
    ]);
    setErrors((prev) => [...prev, {}]);
    setImagePreviews((prev) => [...prev, []]);
    setLocationTextByForm((prev) => [...prev, ""]);
    setActiveIndex(forms.length);
    setAppearingIndex(forms.length);
    setTimeout(() => setAppearingIndex(null), 200);
  };

  const removeListingForm = (index) => {
    if (forms.length <= 1) return;
    setRemovingIndex(index);
    setTimeout(() => {
      setImagePreviews((prev) => {
        const arr = [...prev];
        try {
          (arr[index] || []).forEach((u) => URL.revokeObjectURL(u));
        } catch {}
        return arr.filter((_, i) => i !== index);
      });
      setForms((prev) => prev.filter((_, i) => i !== index));
      setErrors((prev) => prev.filter((_, i) => i !== index));
      setLocationTextByForm((prev) => prev.filter((_, i) => i !== index));
      setActiveIndex((prev) => {
        if (prev === index) return Math.max(0, index - 1);
        if (prev > index) return prev - 1;
        return prev;
      });
      setRemovingIndex(null);
    }, 180);
  };

  const onSubmit = async (e, index) => {
    e.preventDefault();
    setGeneralError("");
    updateError(index, {});

    // Double-check verified listings before submission
    const currentVerified = profile?.verifiedNumberOfListings || 0;
    if (currentVerified === 0) {
      setGeneralError(
        "You need a verified listing package to create listings. Please go to your profile and select a package."
      );
      return;
    }

    const f = forms[index];

    // ✅ Phone number validation (digits only, exactly 10)
    if (!/^[0-9]{10}$/.test(f.phoneNumber.trim())) {
      setFieldError(
        index,
        "phoneNumber",
        "Please enter a valid 10-digit phone number"
      );
      return;
    }

    setSubmitting(true);

    // Prepare form data
    const fd = new FormData();
    fd.append("name", f.name);
    fd.append("price", f.price);
    fd.append("description", f.description);
    fd.append("phoneNumber", f.phoneNumber);
    fd.append("address", f.address);
    fd.append("locationLat", f.locationLat);
    fd.append("locationLng", f.locationLng);
    fd.append("categoryId", f.categoryId);
    fd.append("subcategoryId", f.subcategoryId);
    if (f.maxRadiusKm) fd.append("maxRadiusKm", f.maxRadiusKm);
    for (const file of f.images || []) fd.append("images", file);

    try {
      const token = getAccessToken();
      if (!token) {
        setGeneralError("Authentication token missing. Please log in again.");
        return;
      }

      const response = await providerApi.createListing(fd);

      if (response) {
        setGeneralError("");

        // Reset the form but keep address, phone number, and location from defaults
        updateForm(index, {
          name: "",
          price: "",
          description: "",
          phoneNumber: defaultPhoneNumber || "",
          address: defaultAddress || "",
          locationLat: defaultLocationLat || "",
          locationLng: defaultLocationLng || "",
          categoryId: "",
          subcategoryId: "",
          maxRadiusKm: "",
          images: [],
        });

        // Reset image previews
        const newPreviews = [...imagePreviews];
        newPreviews[index] = [];
        setImagePreviews(newPreviews);

        // Reset location text
        const newLocationTexts = [...locationTextByForm];
        newLocationTexts[index] = "";
        setLocationTextByForm(newLocationTexts);

        // Show success message
        setGeneralError(`✅ Listing ${index + 1} submitted for approval!`);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setGeneralError("");
        }, 3000);
      }
    } catch (error) {
      console.error("Create listing error:", error);
      let errorMessage = "Failed to create listing. Please try again.";
      if (error?.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      }
      setGeneralError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const currencyHint = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" })
        .format(0)
        .replace("0.00", "amount"),
    []
  );

  useLayoutEffect(() => {
    setFormEntering(true);
    const id = requestAnimationFrame(() => setFormEntering(false));
    return () => cancelAnimationFrame(id);
  }, [activeIndex]);

  function setFieldError(index, field, message) {
    setErrors((prev) => {
      const arr = [...prev];
      const merged = { ...(arr[index] || {}) };
      if (message) merged[field] = message;
      else delete merged[field];
      arr[index] = merged;
      return arr;
    });
  }

  function addFiles(index, filesList) {
    const incoming = Array.from(filesList || []);
    const onlyImages = incoming.filter((f) =>
      ["image/jpeg", "image/png"].includes(f.type)
    );
    const current = forms[index]?.images || [];
    const remainingSlots = Math.max(0, 3 - current.length);
    const toAdd = onlyImages.slice(0, remainingSlots);
    if (onlyImages.length > remainingSlots) {
      setFieldError(
        index,
        "images",
        `You can upload up to 3 images. ${
          remainingSlots === 0
            ? "No slots left"
            : `Only ${remainingSlots} more allowed`
        }.`
      );
    } else {
      setFieldError(index, "images", null);
    }
    if (toAdd.length === 0) return;
    setImagePreviews((prev) => {
      const arr = [...prev];
      const existing = arr[index] || [];
      arr[index] = [...existing, ...toAdd.map((f) => URL.createObjectURL(f))];
      return arr;
    });
    updateForm(index, { images: [...current, ...toAdd] });
  }

  function handleImagesChange(index, fileList) {
    addFiles(index, fileList);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeImage(index, imgIdx) {
    const files = forms[index]?.images || [];
    if (!files[imgIdx]) return;
    const nextFiles = files.filter((_, i) => i !== imgIdx);
    updateForm(index, { images: nextFiles });
    setImagePreviews((prev) => {
      const arr = [...prev];
      const list = arr[index] || [];
      const url = list[imgIdx];
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }
      arr[index] = list.filter((_, i) => i !== imgIdx);
      return arr;
    });
    setFieldError(index, "images", null);
  }

  function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "";
    const thresh = 1024;
    if (Math.abs(bytes) < thresh) return bytes + " B";
    const units = ["KB", "MB", "GB", "TB"];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(bytes < 10 ? 1 : 0) + " " + units[u];
  }

  // Check if provider has verified listings
  const verifiedListings = profile?.verifiedNumberOfListings || 0;
  const hasVerifiedListings = verifiedListings > 0;

  // Show loading while checking profile
  if (profileLoading) {
    return (
      <section className="my-6 poppins-regular">
        <div className="flex items-center justify-center min-h-[400px]">
          <CircularProgress />
        </div>
      </section>
    );
  }

  // Block access if no verified listings
  if (!hasVerifiedListings) {
    return (
      <section className="my-6 poppins-regular">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4 text-gray-800">
              Listing Package Required
            </h2>
            <div className="mb-6">
              <p className="text-gray-700 text-lg mb-4">
                You need to have a verified listing package to create listings.
              </p>
              <p className="text-gray-600 mb-2">
                Your current verified listings: <strong>0</strong>
              </p>
              {profile?.paymentStatus === "pending" && (
                <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                  Your payment is pending verification. Once verified by admin,
                  you'll be able to create listings.
                </p>
              )}
              {profile?.numberOfListings && profile?.numberOfListings > 0 ? (
                <p className="text-blue-700 bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  You have selected a package ({profile.numberOfListings}{" "}
                  listings), but it's pending admin verification.
                </p>
              ) : (
                <p className="text-gray-600 mb-4">
                  Please select a listing package from your profile page.
                </p>
              )}
            </div>
            <a
              href="/provider/profile"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Profile
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="my-6 poppins-regular">
      <h2 className="text-3xl font-bold my-4 text-gray-800 text-center">
        Create Listings
      </h2>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-2 mb-0">
        <div
          role="tablist"
          className="flex items-center gap-2 overflow-x-auto overflow-y-visible relative z-10 pb-0"
        >
          {forms.map((f, i) => (
            <div
              key={`tab-${i}`}
              role="tab"
              aria-selected={activeIndex === i}
              onClick={() => setActiveIndex(i)}
              className={`px-3 py-2 rounded-t-md cursor-pointer select-none border inline-flex items-center transition-all duration-200 ease-out transform ${
                activeIndex === i
                  ? "bg-white text-gray-900 border-gray-300"
                  : "bg-gray-100 text-gray-700 border-transparent"
              } ${
                appearingIndex === i || removingIndex === i
                  ? "opacity-0 scale-95"
                  : "opacity-100 scale-100"
              }`}
            >
              <span>Listing {i + 1}</span>
              {forms.length > 1 && (
                <span
                  role="button"
                  aria-label={`Remove Listing ${i + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeListingForm(i);
                  }}
                  className="ml-2 text-red-600 hover:text-red-700"
                >
                  ×
                </span>
              )}
            </div>
          ))}
          <div
            role="tab"
            aria-disabled={forms.length >= 5}
            onClick={() => {
              if (forms.length < 5) addListingForm();
            }}
            className={`px-3 py-2 rounded-t-md cursor-pointer select-none border inline-flex items-center ${
              forms.length >= 5
                ? "bg-gray-200 text-gray-400"
                : "bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
            }`}
          >
            + New
          </div>
          <span className="ml-auto text-sm text-gray-500">
            {forms.length}/5
          </span>
        </div>
        <div className="h-px bg-gray-300 -mt-px mb-3" />
      </div>
      {generalError && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200 max-w-5xl mx-auto">
          {generalError}
        </div>
      )}

      {/* Active form */}
      {(() => {
        const form = forms[activeIndex] || forms[0];
        const formErrors = errors[activeIndex] || {};
        const subcats = form?.categoryId
          ? subcategoriesByCat[form.categoryId] || []
          : [];
        return (
          <form
            key={activeIndex}
            onSubmit={(e) => onSubmit(e, activeIndex)}
            className={`space-y-6 max-w-5xl mx-auto p-6 bg-white rounded-lg shadow border border-gray-300 -mt-px transition-all duration-200 ease-out transform ${
              formEntering
                ? "opacity-0 translate-y-1"
                : "opacity-100 translate-y-0"
            }`}
          >
            <div
              className={`transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "0ms" }}
            >
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                className="mt-1 w-full border p-2 rounded"
                value={form.name}
                onChange={(e) =>
                  updateForm(activeIndex, { name: e.target.value })
                }
                required
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "40ms" }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Price
                </label>
                <input
                  type="number"
                  className="mt-1 w-full border p-2 rounded"
                  value={form.price}
                  onChange={(e) =>
                    updateForm(activeIndex, { price: e.target.value })
                  }
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Amount in INR ({currencyHint})
                </p>
                {formErrors.price && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.price}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max Service Radius (km) — optional
                </label>
                <input
                  type="number"
                  className="mt-1 w-full border p-2 rounded"
                  value={form.maxRadiusKm}
                  onChange={(e) =>
                    updateForm(activeIndex, { maxRadiusKm: e.target.value })
                  }
                  min={1}
                />
                {formErrors.maxRadiusKm && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.maxRadiusKm}
                  </p>
                )}
              </div>
            </div>

            <div
              className={`transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                className="mt-1 w-full border p-2 rounded"
                rows={4}
                value={form.description}
                onChange={(e) =>
                  updateForm(activeIndex, { description: e.target.value })
                }
                required
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description}
                </p>
              )}
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "120ms" }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="mt-1 w-full border p-2 rounded"
                  value={form.phoneNumber || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    // ✅ Allow only digits
                    if (/^\d*$/.test(value)) {
                      updateForm(activeIndex, { phoneNumber: value });
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    let pastedText = (
                      e.clipboardData.getData("text") || ""
                    ).replace(/\D/g, "");
                    // Remove leading +91 or 91 if present (Indian country code)
                    if (pastedText.startsWith("91") && pastedText.length > 10) {
                      pastedText = pastedText.slice(2);
                    }
                    // Limit to 10 digits
                    pastedText = pastedText.slice(0, 10);
                    updateForm(activeIndex, { phoneNumber: pastedText });
                  }}
                  maxLength={10} // ✅ Prevent entering more than 10 digits
                  required
                />
                {formErrors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.phoneNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  className="mt-1 w-full border p-2 rounded"
                  value={form.address || ""}
                  onChange={(e) =>
                    updateForm(activeIndex, { address: e.target.value })
                  }
                  required
                />
                {formErrors.address && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.address}
                  </p>
                )}
              </div>
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "160ms" }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  className="mt-1 w-full border p-2 rounded"
                  value={form.categoryId}
                  onChange={async (e) => {
                    const value = e.target.value;
                    updateForm(activeIndex, {
                      categoryId: value,
                      subcategoryId: "",
                    });
                    await ensureSubcategories(value);
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {formErrors.categoryId && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.categoryId}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subcategory
                </label>
                <select
                  className="mt-1 w-full border p-2 rounded"
                  value={form.subcategoryId}
                  onChange={(e) =>
                    updateForm(activeIndex, { subcategoryId: e.target.value })
                  }
                  required
                  disabled={!form.categoryId}
                >
                  <option value="">Select Subcategory</option>
                  {(subcats || []).map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {formErrors.subcategoryId && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.subcategoryId}
                  </p>
                )}
              </div>
            </div>

            <div
              className={`transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "240ms" }}
            >
              <label className="block text-sm font-medium text-gray-700">
                Location
              </label>
              <div className="mt-1 flex flex-col md:flex-row gap-2 items-start">
                {/* Autocomplete */}
                <div className="flex-1 min-w-0 w-full md:w-auto">
                  <Autocomplete
                    key={`loc-${activeIndex}`}
                    freeSolo
                    options={locationOptions}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    inputValue={locationTextByForm[activeIndex] || ""}
                    value={null}
                    disablePortal={false}
                    onInputChange={(_e, value) =>
                      setLocationText(activeIndex, value)
                    }
                    onChange={(_e, value) => {
                      if (value && typeof value !== "string") {
                        updateForm(activeIndex, {
                          locationLat: String(value.lat),
                          locationLng: String(value.lng),
                        });
                        setLocationText(activeIndex, value.label || "");
                        setFieldError(activeIndex, "locationLat", null);
                        setFieldError(activeIndex, "locationLng", null);
                      }
                    }}
                    loading={locationLoading}
                    loadingText="Loading locations..."
                    PopperProps={{
                      sx: { zIndex: 9999 },
                      className: "z-50",
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search location"
                        placeholder="City, state or locality"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {locationLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        fullWidth
                      />
                    )}
                  />
                </div>

                {/* Latitude */}
                <input
                  className="flex-1 border p-2 rounded w-full md:w-auto"
                  placeholder="Latitude"
                  value={form.locationLat || ""}
                  onChange={(e) =>
                    updateForm(activeIndex, { locationLat: e.target.value })
                  }
                  required
                />

                {/* Longitude */}
                <input
                  className="flex-1 border p-2 rounded w-full md:w-auto"
                  placeholder="Longitude"
                  value={form.locationLng || ""}
                  onChange={(e) =>
                    updateForm(activeIndex, { locationLng: e.target.value })
                  }
                  required
                />
              </div>
              {(formErrors.locationLat || formErrors.locationLng) && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.locationLat || formErrors.locationLng}
                </p>
              )}
            </div>

            <div
              className={`transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "280ms" }}
            >
              <label className="block text-sm font-medium text-gray-700">
                Images
              </label>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  addFiles(activeIndex, e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`mt-1 w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="text-sm text-gray-700">
                  Drag & drop images here, or click to browse
                </div>
                <div className="text-xs text-gray-500">
                  Up to 3 images (JPG/PNG), max ~5MB each
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) =>
                    handleImagesChange(activeIndex, e.target.files)
                  }
                />
              </div>
              {formErrors.images && (
                <p className="mt-2 text-sm text-red-600">{formErrors.images}</p>
              )}
              <div className="mt-3 space-y-2">
                {(forms[activeIndex]?.images || []).length === 0 && (
                  <div className="text-xs text-gray-500">
                    No images selected
                  </div>
                )}
                {(forms[activeIndex]?.images || []).map((file, i) => {
                  const src = (imagePreviews[activeIndex] || [])[i];
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-2 border rounded bg-white"
                    >
                      <div className="h-12 w-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                        {src ? (
                          <img
                            src={src}
                            alt={file.name}
                            className="object-cover h-full w-full"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            No preview
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-sm text-gray-800 truncate"
                          title={file.name}
                        >
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatBytes(file.size)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(activeIndex, i)}
                        className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className={`pt-2 flex items-center gap-3 transition-all duration-200 ease-out transform ${
                formEntering
                  ? "opacity-0 translate-y-1"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ transitionDelay: "320ms" }}
            >
              <button
                type="submit"
                disabled={submitting}
                className={`px-6 py-2 bg-blue-600 text-white rounded ${
                  submitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
              <span className="text-sm text-gray-500">
                Submit each listing separately
              </span>
            </div>
          </form>
        );
      })()}
    </section>
  );
}
